import { 
  TimeToValueInputs, 
  AttributionFactors, 
  ImplementationInvestment,
  Savings,
  PaybackMetrics
} from '../types';

interface MonthlyProjection {
  month: number;
  savings: number;
  cumulativeSavings: number;
  investment: number;
  netBenefit: number;
}

/**
 * Calculate the monthly savings ramp based on implementation and adoption timeline
 */
export function calculateMonthlyRamp(
  baseMonthlyBenefit: number,
  timeToValue: TimeToValueInputs
): number[] {
  const monthlyRamp: number[] = [];
  const implementationMonths = Math.ceil(timeToValue.implementationWeeks / 4.33);
  
  for (let month = 1; month <= 12; month++) {
    let savings = 0;
    
    if (month <= implementationMonths) {
      // During implementation
      if (month === 1) {
        // First month ALWAYS 0% - nothing deployed yet
        savings = 0;
      } else if (timeToValue.earlyWinsEnabled && implementationMonths > 1) {
        // Early wins only kick in after first month
        const implementationProgress = (month - 1) / (implementationMonths - 1);
        // Ramp from 0% to 30% over remaining implementation months
        savings = baseMonthlyBenefit * (0.3 * implementationProgress);
      } else {
        savings = 0;
      }
    } else if (month <= implementationMonths + timeToValue.adoptionRampMonths) {
      // During adoption ramp
      const adoptionMonth = month - implementationMonths;
      const adoptionProgress = adoptionMonth / timeToValue.adoptionRampMonths;
      
      // Start from where implementation ended
      const startPercent = (timeToValue.earlyWinsEnabled && implementationMonths > 1) ? 0.3 : 0;
      // Reach 60% by end of adoption (or 50% if no early wins)
      const targetPercent = timeToValue.earlyWinsEnabled ? 0.6 : 0.5;
      
      savings = baseMonthlyBenefit * (startPercent + ((targetPercent - startPercent) * adoptionProgress));
    } else {
      // Post-adoption: Continue ramping to 100% over next 3 months
      const postAdoptionMonth = month - implementationMonths - timeToValue.adoptionRampMonths;
      const remainingRampMonths = 3; // Take 3 more months to reach 100%
      
      if (postAdoptionMonth <= remainingRampMonths) {
        // Continue ramping from where adoption ended
        const startPercent = timeToValue.earlyWinsEnabled ? 0.6 : 0.5;
        const progress = postAdoptionMonth / remainingRampMonths;
        savings = baseMonthlyBenefit * (startPercent + ((1 - startPercent) * progress));
      } else {
        // Full benefit achieved
        savings = baseMonthlyBenefit;
      }
    }
    
    // Apply operational realization factor to all savings in Year 1
    // This represents the reality that not all theoretical benefits are realized
    savings *= timeToValue.operationalRealizationFactor;
    monthlyRamp.push(savings);
  }
  
  return monthlyRamp;
}

/**
 * Apply attribution factors to TTF savings
 * 
 * Attribution factors recognize that not all time-to-fill improvements can be
 * directly attributed to the scheduling platform:
 * - ttfAttributionFactor: What % of TTF improvement is due to better scheduling
 * - roleVariabilityDiscount: Reduction due to variance across different roles
 * 
 * Example: If TTF improves by 10 days, with 20% attribution and 25% variability,
 * the platform gets credit for: 10 × 0.2 × (1 - 0.25) = 1.5 days
 */
export function applyAttributionFactors(
  baseTTFSavings: number,
  attributionFactors: AttributionFactors
): number {
  const adjustedSavings = baseTTFSavings * 
    attributionFactors.ttfAttributionFactor * 
    (1 - attributionFactors.roleVariabilityDiscount);
  
  return adjustedSavings;
}

/**
 * Calculate implementation investment with defensible assumptions
 * 
 * Implementation cost = Internal effort + Vendor support
 * - Internal effort scales linearly with timeline duration
 * - Longer implementations = more project management, training, change mgmt
 * - Vendor support estimated at 10% of annual subscription
 */
export function calculateImplementationInvestment(
  investment: ImplementationInvestment,
  hiddenSubscription: number,
  implementationWeeks?: number
): number {
  if (investment.mode === 'auto') {
    // Defensible calculation: Internal effort = hours per week × weeks × loaded hourly rate
    // Assume 20 hours/week of internal effort (0.5 FTE) at $150/hour loaded cost
    // This represents project management, training, change management, etc.
    const hoursPerWeek = 20; // Half-time effort during implementation
    const loadedHourlyRate = 150; // Typical loaded cost for professional staff
    
    if (implementationWeeks) {
      // Linear scaling: 2x time = 2x cost (defensible)
      const internalEffortCost = hoursPerWeek * implementationWeeks * loadedHourlyRate;
      
      // Add 10% of annual subscription as vendor support/training cost
      const vendorSupportCost = hiddenSubscription * 0.1;
      
      return internalEffortCost + vendorSupportCost;
    }
    
    // Fallback: 20% of annual subscription if no weeks specified
    return hiddenSubscription * 0.2;
  } else {
    // Custom mode
    if (investment.customAmount) {
      return investment.customAmount;
    }
    
    // Calculate from role allocations
    let totalCost = 0;
    if (investment.sponsorHours && investment.hourlyRates?.sponsor) {
      totalCost += investment.sponsorHours * investment.hourlyRates.sponsor;
    }
    if (investment.opsHours && investment.hourlyRates?.ops) {
      totalCost += investment.opsHours * investment.hourlyRates.ops;
    }
    if (investment.itHours && investment.hourlyRates?.it) {
      totalCost += investment.itHours * investment.hourlyRates.it;
    }
    if (investment.trainingHours && investment.hourlyRates?.training) {
      totalCost += investment.trainingHours * investment.hourlyRates.training;
    }
    if (investment.miscHours && investment.hourlyRates?.misc) {
      totalCost += investment.miscHours * investment.hourlyRates.misc;
    }
    
    return totalCost;
  }
}

/**
 * Calculate Year 1 savings with all reduction factors
 * 
 * Year 1 savings are adjusted by multiple factors:
 * 
 * 1. TIME REDUCTION (all savings):
 *    - Implementation period: 0% or 50% benefit (if early wins enabled)
 *    - Adoption ramp period: 50% benefit
 *    - Full adoption period: 100% benefit
 * 
 * 2. ATTRIBUTION FACTORS (TTF savings only):
 *    - TTF Attribution: % of improvement attributable to scheduling platform
 *    - Role Variability: Discount for variance across different roles
 * 
 * 3. OPERATIONAL REALIZATION (all savings):
 *    - Represents the reality that not all theoretical benefits are captured
 *    - Conservative: 70%, Moderate: 80%, Optimistic: 90%
 * 
 * This ensures alignment with monthly projections which apply the same factors.
 */
export function calculateYear1Savings(
  baseSavings: Savings,
  timeToValue: TimeToValueInputs,
  attributionFactors: AttributionFactors
): Savings {
  const implementationMonths = Math.ceil(timeToValue.implementationWeeks / 4.33);
  const rampMonths = timeToValue.adoptionRampMonths;
  const fullMonths = Math.max(0, 12 - implementationMonths - rampMonths);
  
  // Calculate effective months of benefit
  let effectiveMonths = 0;
  if (timeToValue.earlyWinsEnabled) {
    effectiveMonths += implementationMonths * 0.5;  // 50% benefit during implementation
  }
  effectiveMonths += rampMonths * 0.5;  // 50% benefit during adoption ramp
  effectiveMonths += fullMonths;  // 100% benefit after full adoption
  
  // Apply time-based reduction
  const timeReductionFactor = effectiveMonths / 12;
  
  // Apply attribution to TTF savings
  const adjustedTTFSavings = applyAttributionFactors(
    baseSavings.timeToFillSavings,
    attributionFactors
  );
  
  // Apply all factors to ensure alignment with monthly projections:
  // - Time reduction factor: Based on implementation timeline and adoption ramp
  // - Operational realization: Applied to ALL savings for consistency
  // Note: While conceptually TTF improvements might not need operational realization
  // (they're more binary), we apply it for calculation consistency across the system
  return {
    timeToFillSavings: adjustedTTFSavings * timeReductionFactor * timeToValue.operationalRealizationFactor,
    costPerHireSavings: baseSavings.costPerHireSavings * timeReductionFactor * timeToValue.operationalRealizationFactor,
    productivityGain: baseSavings.productivityGain * timeReductionFactor * timeToValue.operationalRealizationFactor,
    schedulingTimeSavings: baseSavings.schedulingTimeSavings * timeReductionFactor * timeToValue.operationalRealizationFactor,
    noShowReductionSavings: baseSavings.noShowReductionSavings * timeReductionFactor * timeToValue.operationalRealizationFactor,
    totalAnnualSavings: 0 // Will be calculated after
  };
}

/**
 * Calculate steady-state savings (Year 2+)
 */
export function calculateSteadyStateSavings(
  baseSavings: Savings,
  attributionFactors: AttributionFactors
): Savings {
  // Apply attribution to TTF savings only
  const adjustedTTFSavings = applyAttributionFactors(
    baseSavings.timeToFillSavings,
    attributionFactors
  );
  
  return {
    ...baseSavings,
    timeToFillSavings: adjustedTTFSavings,
    totalAnnualSavings: adjustedTTFSavings + 
      baseSavings.costPerHireSavings +
      baseSavings.productivityGain +
      baseSavings.schedulingTimeSavings +
      baseSavings.noShowReductionSavings
  };
}

/**
 * Calculate payback period and metrics
 */
export function calculatePaybackPeriod(
  monthlyProjections: MonthlyProjection[],
  totalInvestment: number
): PaybackMetrics {
  let paybackMonth = 0;
  const cumulativeSavingsByMonth: number[] = [];
  const monthlyROI: number[] = [];
  
  for (let i = 0; i < monthlyProjections.length; i++) {
    const projection = monthlyProjections[i];
    cumulativeSavingsByMonth.push(projection.cumulativeSavings);
    
    // Calculate monthly ROI
    const monthlyInvestment = i === 0 ? totalInvestment : 0;
    const roi = monthlyInvestment > 0 ? 
      (projection.savings / monthlyInvestment) * 100 : 
      projection.savings > 0 ? 100 : 0;
    monthlyROI.push(roi);
    
    // Find payback month based on net benefit
    if (paybackMonth === 0 && projection.netBenefit >= 0) {
      paybackMonth = i + 1;
    }
  }
  
  // If no payback in Year 1, estimate based on trend
  if (paybackMonth === 0 && monthlyProjections.length > 0) {
    const lastProjection = monthlyProjections[monthlyProjections.length - 1];
    const monthlySavingsRate = lastProjection.savings;
    const monthlySubscription = lastProjection.investment; // This is just the subscription in months 2+
    const netMonthlySavings = monthlySavingsRate - monthlySubscription;
    
    if (netMonthlySavings > 0) {
      // Calculate remaining deficit
      const remainingDeficit = -lastProjection.netBenefit;
      paybackMonth = Math.ceil(12 + (remainingDeficit / netMonthlySavings));
    }
  }
  
  return {
    paybackMonth,
    cumulativeSavingsByMonth,
    monthlyROI
  };
}

/**
 * Generate complete monthly projections
 */
export function generateMonthlyProjections(
  baseSavings: Savings,
  timeToValue: TimeToValueInputs,
  attributionFactors: AttributionFactors,
  implementationInvestment: number,
  hiddenSubscription: number
): MonthlyProjection[] {
  const projections: MonthlyProjection[] = [];
  const monthlySubscription = hiddenSubscription / 12;
  
  // Apply attribution to TTF savings (attribution factors affect only TTF)
  const adjustedMonthlyTTF = applyAttributionFactors(
    baseSavings.timeToFillSavings / 12,
    attributionFactors
  );
  
  // All operational savings (including productivity gain) get operational realization factor
  // applied through calculateMonthlyRamp
  const monthlyOperationalSavings = (
    baseSavings.costPerHireSavings +
    baseSavings.schedulingTimeSavings +
    baseSavings.noShowReductionSavings +
    baseSavings.productivityGain  // Productivity gain is an operational saving
  ) / 12;
  
  const adjustedMonthlyBase = adjustedMonthlyTTF + monthlyOperationalSavings;
  const monthlyRamp = calculateMonthlyRamp(adjustedMonthlyBase, timeToValue);
  
  let cumulativeSavings = 0;
  
  for (let month = 0; month < 12; month++) {
    const savings = monthlyRamp[month];
    cumulativeSavings += savings;
    
    // Investment is one-time in month 1, then just subscription
    const investment = month === 0 ? 
      implementationInvestment + monthlySubscription : 
      monthlySubscription;
    
    projections.push({
      month: month + 1,
      savings,
      cumulativeSavings,
      investment,
      netBenefit: cumulativeSavings - (implementationInvestment + (monthlySubscription * (month + 1)))
    });
  }
  
  return projections;
}

/**
 * Get scenario presets
 */
export function getScenarioPresets(scenarioMode: 'conservative' | 'moderate' | 'optimistic', includeTimeline: boolean = false): {
  timeToValue: Partial<TimeToValueInputs>;
  attributionFactors: Partial<AttributionFactors>;
  investmentApproach?: 'minimal' | 'standard' | 'comprehensive';
} {
  switch (scenarioMode) {
    case 'conservative':
      return {
        timeToValue: {
          // Conservative: Longer timeline for risk mitigation
          adoptionRampMonths: 3,    // Always set slower adoption for conservative
          ...(includeTimeline ? {
            implementationWeeks: 18,  // Tailored: 4-5 months
          } : {}),
          operationalRealizationFactor: 0.7,
          earlyWinsEnabled: false
        },
        attributionFactors: {
          ttfAttributionFactor: 0.15,
          roleVariabilityDiscount: 0.25  // Conservative: Higher uncertainty in hiring patterns
        },
        ...(includeTimeline ? { investmentApproach: 'comprehensive' as const } : {})
      };
    
    case 'optimistic':
      return {
        timeToValue: {
          // Optimistic: Faster timeline with accelerated adoption
          adoptionRampMonths: 1,    // Always set quick adoption for optimistic
          ...(includeTimeline ? {
            implementationWeeks: 6,   // Standard: 1-2 months
          } : {}),
          operationalRealizationFactor: 0.9,
          earlyWinsEnabled: false  // Rely on faster timeline and higher realization, not early wins
        },
        attributionFactors: {
          ttfAttributionFactor: 0.3,
          roleVariabilityDiscount: 0.1  // Optimistic: Low uncertainty with clear hiring plans
        },
        ...(includeTimeline ? { investmentApproach: 'minimal' as const } : {})
      };
    
    case 'moderate':
    default:
      return {
        timeToValue: {
          // Moderate: Balanced timeline based on industry standards
          adoptionRampMonths: 2,    // Always set standard adoption for moderate
          ...(includeTimeline ? {
            implementationWeeks: 6,   // Standard: 1-2 months (default for balanced scenario)
          } : {}),
          operationalRealizationFactor: 0.8,
          earlyWinsEnabled: false
        },
        attributionFactors: {
          ttfAttributionFactor: 0.2,
          roleVariabilityDiscount: 0.15  // Moderate: Typical variance in hiring patterns
        },
        ...(includeTimeline ? { investmentApproach: 'standard' as const } : {})
      };
  }
}

/**
 * Apply pilot mode adjustments
 */
export function applyPilotModeAdjustments(
  timeToValue: TimeToValueInputs,
  attributionFactors: AttributionFactors
): {
  timeToValue: TimeToValueInputs;
  attributionFactors: AttributionFactors;
} {
  return {
    timeToValue: {
      ...timeToValue,
      implementationWeeks: Math.min(timeToValue.implementationWeeks, 8),
      adoptionRampMonths: Math.min(timeToValue.adoptionRampMonths, 2)
    },
    attributionFactors: {
      ttfAttributionFactor: Math.min(attributionFactors.ttfAttributionFactor, 0.2),
      roleVariabilityDiscount: Math.max(attributionFactors.roleVariabilityDiscount, 0.2)  // Reduced from 0.3 due to hiring profile clarity
    }
  };
}